import type { CSSProperties } from 'react';

import { TypographyH3 } from '@talentmesh/ui/components/typography';
import { cn } from '@talentmesh/ui/lib/utils';
import { PercentileLevel } from '@talentmesh/utils';

interface AnimatedCircularProgressBarProps {
    value?: number;
    level?: PercentileLevel;
    className?: string;
    text?: string;
}

const strokeColors = {
    VeryLow: 'stroke-percentile-very-low',
    Low: 'stroke-percentile-low',
    Medium: 'stroke-percentile-medium',
    High: 'stroke-percentile-high',
    Top: 'stroke-percentile-top',
};

export function AnimatedCircularProgressBar({ value, level, className, text }: AnimatedCircularProgressBarProps) {
    const circumference = 2 * Math.PI * 45;
    const percentPx = circumference / 100;
    const currentPercent = Math.round(Math.min(100, Math.max(0, value ?? 0)));

    const showTextOverlay = Boolean(text);

    return (
        <div
            className={cn('relative flex size-40 items-center justify-center text-2xl font-semibold', className)}
            style={
                {
                    '--circle-size': '100px',
                    '--circumference': circumference,
                    '--percent-to-px': `${percentPx}px`,
                    '--gap-percent': '0',
                    '--offset-factor': '0',
                    '--transition-length': '1s',
                    '--transition-step': '200ms',
                    '--delay': '0s',
                    '--percent-to-deg': '3.6deg',
                    transform: 'translateZ(0)',
                } as CSSProperties
            }
        >
            <svg fill="none" className="size-full" strokeWidth="2" viewBox="0 0 100 100">
                <circle
                    cx="50"
                    cy="50"
                    r="45"
                    strokeWidth="10"
                    strokeDashoffset="0"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={cn('opacity-100', 'stroke-gray-300')}
                    style={
                        {
                            '--stroke-percent': 100 - currentPercent,
                            '--offset-factor-secondary': 'calc(1 - var(--offset-factor))',
                            strokeDasharray: 'calc(var(--stroke-percent) * var(--percent-to-px)) var(--circumference)',
                            transform:
                                'rotate(calc(1turn - 90deg - (var(--gap-percent) * var(--percent-to-deg) * var(--offset-factor-secondary)))) scaleY(-1)',
                            transition: 'all var(--transition-length) ease var(--delay)',
                            transformOrigin: 'calc(var(--circle-size) / 2) calc(var(--circle-size) / 2)',
                        } as CSSProperties
                    }
                />
                {currentPercent <= 100 && currentPercent > 0 && (
                    <circle
                        cx="50"
                        cy="50"
                        r="45"
                        strokeWidth="10"
                        strokeDashoffset="0"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={cn(level ? strokeColors[level] : 'stroke-gray-300')}
                        style={
                            {
                                '--stroke-percent': currentPercent,
                                strokeDasharray:
                                    'calc(var(--stroke-percent) * var(--percent-to-px)) var(--circumference)',
                                transition:
                                    'var(--transition-length) ease var(--delay),stroke var(--transition-length) ease var(--delay)',
                                transitionProperty: 'stroke-dasharray,transform',
                                transform:
                                    'rotate(calc(-90deg + var(--gap-percent) * var(--offset-factor) * var(--percent-to-deg)))',
                                transformOrigin: 'calc(var(--circle-size) / 2) calc(var(--circle-size) / 2)',
                            } as CSSProperties
                        }
                    />
                )}
            </svg>
            {showTextOverlay ? (
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-0 px-2">
                    <TypographyH3 className="text-center text-xl text-inherit">{text}</TypographyH3>
                    <div className="flex flex-row items-baseline gap-0">
                        <span className="text-5xl font-semibold text-inherit">{value ?? '-'}</span>
                    </div>
                </div>
            ) : null}
            {!showTextOverlay ? (
                <span
                    data-current-value={currentPercent}
                    className="absolute inset-0 m-auto size-fit delay-(--delay) duration-(--transition-length) ease-linear"
                >
                    {value === undefined || value === null ? '-' : currentPercent}
                </span>
            ) : null}
        </div>
    );
}
